import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ShellAtnaTurn, ShellTurn } from '../types/atna'

export type CollecteKind = 'requete-ouverte' | 'reponse' | 'suivi'

const THINKING_MS = 800

/** Reconnaît une phrase qui décrit un bac laissé sur le bord de la rue. */
export function isMissedBinReport(text: string): boolean {
  const lower = text.toLowerCase()
  return /bac|poubelle|bin/.test(lower) && /non ramass|pas ramass|oubli|pas vid|encore plein/.test(lower)
}

/**
 * Flux ATNA du module Collectes : un bac non ramassé ouvre directement une
 * requête auprès des Travaux publics.
 */
export function useCollecteConversation(initialText: string, onOpenRequest?: () => void) {
  const seed = initialText.trim()
  const requestOpened = useRef(false)

  const [turns, setTurns] = useState<ShellTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [photoAttached, setPhotoAttached] = useState(false)

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback(
    (kind: CollecteKind) => {
      if (kind === 'requete-ouverte' && !requestOpened.current) {
        requestOpened.current = true
        onOpenRequest?.()
      }
      if (timerRef.current) window.clearTimeout(timerRef.current)
      setThinking(true)
      timerRef.current = window.setTimeout(() => {
        idRef.current += 1
        setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
        setThinking(false)
      }, THINKING_MS)
    },
    [onOpenRequest],
  )

  const exchange = useCallback(
    (userText: string, kind: CollecteKind, withPhoto = false) => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'user', text: userText, withPhoto }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    reply(isMissedBinReport(seed) ? 'requete-ouverte' : 'reponse')
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is ShellAtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const attachPhoto = useCallback(() => {
    setPhotoAttached(true)
    exchange('Photo du bac ajoutée à la requête', 'suivi', true)
  }, [exchange])

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')
    exchange(text, isMissedBinReport(text) ? 'requete-ouverte' : 'suivi')
  }, [draft, exchange, thinking])

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    attachPhoto,
    photoAttached,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind as CollecteKind | undefined,
  }
}
